import { Inject, Injectable, InjectionToken } from '@angular/core';
import { SsrCookieService as CookieService } from 'ngx-cookie-service-ssr';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { ApiService } from '../../core/services/api.service';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';

export const DOCUMENT = new InjectionToken<Document>('Document', {
  providedIn: 'root',
  factory: () => (typeof document !== 'undefined' ? document : null)
});

@Injectable({
  providedIn: 'root'
})
export class LandingPageTrackerService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private platformService: PlatformInformationService,
    private cookieService: CookieService,
    private localStorageService: LocalStorageService,
    private apiService: ApiService
  ) {}

  /**
   * Returns true, if the given uuid is a valid uuid-string,
   * false if not.
   *
   * https://stackoverflow.com/questions/7905929/how-to-test-valid-uuid-guid
   * @param uuid
   */
  isUuidString(uuid: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
  }

  /**
   * Returns a uuid version 4
   */
  generateUuidv4(): string {
    try {
      return crypto.randomUUID();
    } catch {
      return '';
    }
  }

  /**
   * @TODO: the worker should be provided by a function or service to make this testable
   */
  track() {
    if (this.platformService.getIsBrowser()) {
      if (typeof Worker !== 'undefined') {
        // we are in an environment that can use workers
        const worker = new Worker(new URL('../../crawler.worker', import.meta.url));
        worker.onmessage = ({ data }) => {
          if (data == false) {
            // not a crawler
            this._track();
          }
        };

        worker.postMessage(this.document.defaultView.navigator.userAgent);
      }
    }
  }

  _track() {
    const trackerId = this.getSyncedLandingPageTrackerId();
    const landingPage = this.document.URL;
    this.apiService.trackLandingpage(landingPage, trackerId).subscribe({
      error: err => {
        console.error(err);
      }
    });
  }

  /**
   * Returns a UUIDv4
   * Fetches the UUID from the local-storage or cookie
   * Syncs the local-storage with the cookie-storage
   * Generates a new UUID, if neither the local-storage nor the cookie-storage holds a uuid
   */
  private getSyncedLandingPageTrackerId(): string {
    let trackerId = '';
    let trackerIdFromCookie = this.cookieService.get('artrid');
    let trackerIdFromLocalStorage = this.localStorageService.read('artrid');
    trackerIdFromCookie = this.isUuidString(trackerIdFromCookie) ? trackerIdFromCookie : null;
    trackerIdFromLocalStorage = this.isUuidString(trackerIdFromLocalStorage)
      ? trackerIdFromLocalStorage
      : null;

    if (
      trackerIdFromCookie &&
      trackerIdFromLocalStorage &&
      trackerIdFromCookie === trackerIdFromLocalStorage
    ) {
      return trackerIdFromCookie;
    }

    if (trackerIdFromCookie) {
      this.localStorageService.write('artrid', trackerIdFromCookie);
      trackerId = trackerIdFromCookie;
    } else if (trackerIdFromLocalStorage) {
      this.cookieService.set('artrid', trackerIdFromLocalStorage, { expires: new Date('2038') });
      trackerId = trackerIdFromLocalStorage;
    } else {
      trackerId = this.generateUuidv4();
      this.cookieService.set('artrid', trackerId, { expires: new Date('2038') });
      this.localStorageService.write('artrid', trackerId);
    }
    return trackerId;
  }
}
